// Configures exactly what a driver, admin, or portal user (a non-driver
// login -- e.g. a lease operator's family member helping with bookwork)
// can see and do: which AdminDashboard tabs, read-only or read & edit on
// each, and which specific driver(s)/vehicle(s) their view is scoped to.
// See src/lib/permissions.ts for how this gets resolved into what a
// restricted person's session actually shows.
//
// Safety note: nobody's access changes here unless you explicitly give
// them at least one tab -- see UNRESTRICTED_PERMISSIONS in permissions.ts.
// This screen only ever adds restrictions; a person with nothing
// configured keeps their full, unrestricted access exactly as before.
import React, { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "@/components/ui/use-toast";
import { UserPlus, Shield, Pencil, Eye } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useAppContext } from "@/contexts/AppContext";
import { ALL_MODULE_KEYS, type ModuleKey } from "@/lib/permissions";

const DEFAULT_TENANT_ID = "1225f4cb-43ae-463d-a6b0-53f721362dbf";

const MODULE_LABELS: Record<ModuleKey, string> = {
  closedcheck: "Closed Check",
  matchhub: "Match Hub",
  summary: "Summary",
  timesheets: "Timesheets",
  drivers: "Drivers",
  fleet: "Fleet",
  fleetpulse: "Fleet Pulse",
  fleetcapacity: "Fleet Capacity",
  roster: "Roster",
  jobinput: "Job Input",
  reports: "Reports",
  crm: "CRM",
  exportbuilder: "Export Builder",
  ecard: "eCard",
  payout: "Payout Report",
  insurance: "Insurance",
  ivrcalls: "IVR Calls",
  commsbook: "Comms Book",
  settings: "Settings",
};

interface TenantUserRow {
  id: string;
  driver_name: string | null;
  display_name: string | null;
  email: string | null;
  is_active: boolean;
}

interface VehicleOption {
  vehicle_number: string;
  registration: string | null;
}

interface ModuleAccessDraft {
  can_view: boolean;
  can_edit: boolean;
}

export default function TeamAccessSettings() {
  const { drivers } = useAppContext();

  const [tenantUsers, setTenantUsers] = useState<TenantUserRow[]>([]);
  const [restrictedCounts, setRestrictedCounts] = useState<Record<string, number>>({}); // tenant_user_id -> visible tab count
  const [vehicles, setVehicles] = useState<VehicleOption[]>([]);
  const [loading, setLoading] = useState(true);

  const [editing, setEditing] = useState<TenantUserRow | null>(null);
  const [draftModules, setDraftModules] = useState<Partial<Record<ModuleKey, ModuleAccessDraft>>>({});
  const [driverSelection, setDriverSelection] = useState<Set<string>>(new Set());
  const [vehicleSelection, setVehicleSelection] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState(false);

  const [createOpen, setCreateOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [creating, setCreating] = useState(false);

  const load = async () => {
    setLoading(true);
    const [tenantUsersRes, moduleRes, vehiclesRes] = await Promise.all([
      supabase.from("tenant_users").select("id, driver_name, display_name, email, is_active").eq("tenant_id", DEFAULT_TENANT_ID).eq("is_active", true),
      supabase.from("tenant_user_module_permissions").select("tenant_user_id").eq("is_active", true),
      supabase.from("vehicles").select("vehicle_number, registration").order("vehicle_number"),
    ]);

    setTenantUsers((tenantUsersRes.data as TenantUserRow[]) || []);
    const counts: Record<string, number> = {};
    for (const row of moduleRes.data || []) counts[row.tenant_user_id] = (counts[row.tenant_user_id] || 0) + 1;
    setRestrictedCounts(counts);
    setVehicles((vehiclesRes.data as VehicleOption[]) || []);
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const openEditor = async (tu: TenantUserRow) => {
    setEditing(tu);
    setDraftModules({});
    setDriverSelection(new Set());
    setVehicleSelection(new Set());

    const [moduleRes, driverRes, vehicleRes] = await Promise.all([
      supabase.from("tenant_user_module_permissions").select("module_key, can_view, can_edit").eq("tenant_user_id", tu.id).eq("is_active", true),
      supabase.from("tenant_user_driver_access").select("driver_name").eq("tenant_user_id", tu.id).eq("is_active", true),
      supabase.from("tenant_user_vehicle_access").select("vehicle_number").eq("tenant_user_id", tu.id).eq("is_active", true),
    ]);

    const modules: Partial<Record<ModuleKey, ModuleAccessDraft>> = {};
    for (const row of moduleRes.data || []) {
      modules[row.module_key as ModuleKey] = { can_view: row.can_view, can_edit: row.can_edit };
    }
    setDraftModules(modules);
    setDriverSelection(new Set((driverRes.data || []).map((r) => r.driver_name)));
    setVehicleSelection(new Set((vehicleRes.data || []).map((r) => r.vehicle_number)));
  };

  const toggleModuleVisible = (key: ModuleKey) => {
    setDraftModules((prev) => {
      const next = { ...prev };
      if (next[key]) {
        delete next[key];
      } else {
        next[key] = { can_view: true, can_edit: false };
      }
      return next;
    });
  };

  const setModuleEdit = (key: ModuleKey, canEdit: boolean) => {
    setDraftModules((prev) => ({ ...prev, [key]: { can_view: true, can_edit: canEdit } }));
  };

  const toggleSetMember = (set: Set<string>, setter: (s: Set<string>) => void, value: string) => {
    const next = new Set(set);
    if (next.has(value)) next.delete(value);
    else next.add(value);
    setter(next);
  };

  const handleResetToFullAccess = async () => {
    if (!editing) return;
    setSaving(true);
    const { error } = await supabase
      .from("tenant_user_module_permissions")
      .update({ is_active: false })
      .eq("tenant_user_id", editing.id);
    setSaving(false);
    if (error) {
      toast({ title: "Failed to reset access", variant: "destructive" });
      return;
    }
    toast({ title: `${editing.display_name || editing.driver_name} now has full access again` });
    setEditing(null);
    load();
  };

  const handleSave = async () => {
    if (!editing) return;
    setSaving(true);

    // Modules: deactivate everything currently active, then upsert exactly
    // what's checked now. Simpler and safer than diffing -- this table is
    // only ever touched from this one screen.
    const { error: deactivateError } = await supabase
      .from("tenant_user_module_permissions")
      .update({ is_active: false })
      .eq("tenant_user_id", editing.id);
    if (deactivateError) {
      toast({ title: "Failed to save tab access", variant: "destructive" });
      setSaving(false);
      return;
    }

    const moduleEntries = Object.entries(draftModules) as [ModuleKey, ModuleAccessDraft][];
    if (moduleEntries.length > 0) {
      const { error } = await supabase.from("tenant_user_module_permissions").upsert(
        moduleEntries.map(([module_key, access]) => ({
          tenant_id: DEFAULT_TENANT_ID,
          tenant_user_id: editing.id,
          module_key,
          can_view: access.can_view,
          can_create: access.can_edit,
          can_edit: access.can_edit,
          can_delete: false,
          can_approve: false,
          can_export: access.can_edit,
          is_active: true,
        })),
        { onConflict: "tenant_user_id,module_key" }
      );
      if (error) {
        toast({ title: "Failed to save tab access", variant: "destructive" });
        setSaving(false);
        return;
      }
    }

    // Driver links: same deactivate-then-insert-selected approach.
    await supabase.from("tenant_user_driver_access").update({ is_active: false }).eq("tenant_user_id", editing.id);
    if (driverSelection.size > 0) {
      const { error } = await supabase.from("tenant_user_driver_access").upsert(
        [...driverSelection].map((driver_name) => ({
          tenant_id: DEFAULT_TENANT_ID,
          tenant_user_id: editing.id,
          driver_name,
          access_reason: "Team Access screen",
          can_view: true,
          can_view_driver_details: true,
          can_view_payroll: true,
          is_active: true,
        })),
        { onConflict: "tenant_user_id,driver_name" }
      );
      if (error) {
        toast({ title: "Failed to save linked drivers", variant: "destructive" });
        setSaving(false);
        return;
      }
    }

    // Vehicle links: same pattern.
    await supabase.from("tenant_user_vehicle_access").update({ is_active: false }).eq("tenant_user_id", editing.id);
    if (vehicleSelection.size > 0) {
      const { error } = await supabase.from("tenant_user_vehicle_access").upsert(
        [...vehicleSelection].map((vehicle_number) => ({
          tenant_id: DEFAULT_TENANT_ID,
          tenant_user_id: editing.id,
          vehicle_number,
          access_reason: "Team Access screen",
          can_view: true,
          is_active: true,
        })),
        { onConflict: "tenant_user_id,vehicle_number" }
      );
      if (error) {
        toast({ title: "Failed to save linked vehicles", variant: "destructive" });
        setSaving(false);
        return;
      }
    }

    setSaving(false);
    setEditing(null);
    toast({ title: "Access updated" });
    load();
  };

  const handleCreatePortalUser = async () => {
    if (!newName.trim() || !newEmail.trim() || newPassword.length < 8) {
      toast({ title: "Name, email and an 8+ character password are all required", variant: "destructive" });
      return;
    }
    setCreating(true);
    const { data, error } = await supabase.functions.invoke("create-portal-user", {
      body: { display_name: newName.trim(), email: newEmail.trim(), password: newPassword },
    });
    setCreating(false);
    if (error || data?.error) {
      toast({ title: "Failed to create login", description: data?.error || error?.message, variant: "destructive" });
      return;
    }
    toast({ title: "Login created", description: `Share the email and password with ${newName.trim()} directly.` });
    setCreateOpen(false);
    setNewName("");
    setNewEmail("");
    setNewPassword("");
    load();
  };

  const driverBackedUsers = tenantUsers.filter((tu) => tu.driver_name);
  const portalUsers = tenantUsers.filter((tu) => !tu.driver_name);

  const renderRow = (tu: TenantUserRow, fallbackName: string) => {
    const count = restrictedCounts[tu.id] || 0;
    return (
      <div key={tu.id} className="flex items-center justify-between gap-3 rounded-md border border-border p-3 flex-wrap">
        <div className="min-w-[160px]">
          <p className="font-medium text-foreground">{tu.display_name || fallbackName}</p>
          {tu.email && <p className="text-xs text-muted-foreground">{tu.email}</p>}
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          {count > 0 ? (
            <Badge variant="outline" className="gap-1 text-muted-foreground">
              <Shield className="w-3 h-3" />
              Restricted -- {count} tab{count === 1 ? "" : "s"}
            </Badge>
          ) : (
            <Badge variant="outline">Full access</Badge>
          )}
          <Button variant="outline" size="sm" onClick={() => openEditor(tu)}>
            Manage Access
          </Button>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <Card className="shadow-lg bg-card border border-border">
        <CardHeader>
          <CardTitle className="text-xl text-primary flex items-center gap-2">
            <Shield className="w-5 h-5" />
            Team Access
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            Give someone a limited view of the app -- e.g. a lease operator seeing only their own vehicle, driver record and bookwork, not the
            rest of the business. "Full access" (the default for everyone) is completely unaffected until you configure someone here.
          </p>
        </CardContent>
      </Card>

      <Card className="shadow-lg bg-card border border-border">
        <CardHeader>
          <CardTitle className="text-lg text-foreground">Drivers &amp; Admins</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {loading ? (
            <p className="text-sm text-muted-foreground py-4 text-center">Loading...</p>
          ) : driverBackedUsers.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4 text-center">
              No one's logged in yet -- a driver only shows up here after their first login.
            </p>
          ) : (
            driverBackedUsers.map((tu) => renderRow(tu, tu.driver_name || "Unknown"))
          )}
          {!loading && drivers.some((d) => d.is_active !== false && !driverBackedUsers.some((tu) => tu.driver_name === d.driver_name)) && (
            <p className="text-xs text-muted-foreground pt-2">
              Some active drivers aren't listed above yet -- they'll appear here once they log in for the first time.
            </p>
          )}
        </CardContent>
      </Card>

      <Card className="shadow-lg bg-card border border-border">
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <CardTitle className="text-lg text-foreground">Portal Users</CardTitle>
          <Button size="sm" onClick={() => setCreateOpen(true)}>
            <UserPlus className="w-4 h-4 mr-2" />
            Add Team Member Login
          </Button>
        </CardHeader>
        <CardContent className="space-y-2">
          <p className="text-xs text-muted-foreground pb-2">
            A login for someone who isn't a driver -- e.g. a lease operator's family member helping with their bookwork.
          </p>
          {loading ? (
            <p className="text-sm text-muted-foreground py-4 text-center">Loading...</p>
          ) : portalUsers.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4 text-center">No portal user logins yet.</p>
          ) : (
            portalUsers.map((tu) => renderRow(tu, tu.email || "Portal user"))
          )}
        </CardContent>
      </Card>

      <Dialog open={!!editing} onOpenChange={(open) => !open && setEditing(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Manage Access -- {editing?.display_name || editing?.driver_name}</DialogTitle>
          </DialogHeader>

          <div className="space-y-6">
            <div className="space-y-2">
              <Label>Tabs</Label>
              <p className="text-xs text-muted-foreground">
                Tick a tab to give access to it. Leave everything unticked for full, unrestricted access (the default).
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 rounded-md border border-border p-3">
                {ALL_MODULE_KEYS.map((key) => {
                  const access = draftModules[key];
                  return (
                    <div key={key} className="flex items-center justify-between gap-2 py-0.5">
                      <div className="flex items-center gap-2">
                        <Checkbox id={`mod-${key}`} checked={!!access} onCheckedChange={() => toggleModuleVisible(key)} />
                        <Label htmlFor={`mod-${key}`} className="cursor-pointer font-normal">
                          {MODULE_LABELS[key]}
                        </Label>
                      </div>
                      {access && (
                        <div className="flex items-center bg-background border border-border rounded-md overflow-hidden shrink-0">
                          <button
                            type="button"
                            onClick={() => setModuleEdit(key, false)}
                            className={`px-2 py-1 text-xs flex items-center gap-1 ${!access.can_edit ? "bg-blue-600 text-white" : "text-muted-foreground"}`}
                          >
                            <Eye className="w-3 h-3" /> View
                          </button>
                          <button
                            type="button"
                            onClick={() => setModuleEdit(key, true)}
                            className={`px-2 py-1 text-xs flex items-center gap-1 ${access.can_edit ? "bg-blue-600 text-white" : "text-muted-foreground"}`}
                          >
                            <Pencil className="w-3 h-3" /> Edit
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="space-y-2">
              <Label>Linked Drivers</Label>
              <p className="text-xs text-muted-foreground">Which driver(s)' own data this person's tabs are scoped to.</p>
              <div className="max-h-40 overflow-y-auto space-y-1 rounded-md border border-border p-3">
                {drivers
                  .filter((d) => d.is_active !== false)
                  .map((d) => (
                    <div key={d.driver_name} className="flex items-center gap-2">
                      <Checkbox
                        id={`drv-${d.driver_name}`}
                        checked={driverSelection.has(d.driver_name)}
                        onCheckedChange={() => toggleSetMember(driverSelection, setDriverSelection, d.driver_name)}
                      />
                      <Label htmlFor={`drv-${d.driver_name}`} className="cursor-pointer font-normal">
                        {d.driver_name}
                      </Label>
                    </div>
                  ))}
              </div>
            </div>

            <div className="space-y-2">
              <Label>Linked Vehicles</Label>
              <p className="text-xs text-muted-foreground">Which vehicle(s) this person's Fleet/capacity tabs are scoped to.</p>
              <div className="max-h-40 overflow-y-auto space-y-1 rounded-md border border-border p-3">
                {vehicles.map((v) => (
                  <div key={v.vehicle_number} className="flex items-center gap-2">
                    <Checkbox
                      id={`veh-${v.vehicle_number}`}
                      checked={vehicleSelection.has(v.vehicle_number)}
                      onCheckedChange={() => toggleSetMember(vehicleSelection, setVehicleSelection, v.vehicle_number)}
                    />
                    <Label htmlFor={`veh-${v.vehicle_number}`} className="cursor-pointer font-normal">
                      {v.vehicle_number}
                      {v.registration ? ` -- ${v.registration}` : ""}
                    </Label>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <DialogFooter className="flex items-center justify-between sm:justify-between">
            <Button variant="ghost" onClick={handleResetToFullAccess} disabled={saving} className="text-muted-foreground">
              Reset to Full Access
            </Button>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setEditing(null)}>
                Cancel
              </Button>
              <Button onClick={handleSave} disabled={saving}>
                {saving ? "Saving..." : "Save"}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add Team Member Login</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="portal-name">Name</Label>
              <Input id="portal-name" value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="e.g. Sharon Sandbrook" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="portal-email">Email</Label>
              <Input id="portal-email" type="email" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} placeholder="them@example.com" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="portal-password">Password</Label>
              <Input
                id="portal-password"
                type="text"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="At least 8 characters"
              />
              <p className="text-xs text-muted-foreground">
                Shown in plain text here so you can note it down to share with them -- it isn't stored anywhere but their login.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreatePortalUser} disabled={creating}>
              {creating ? "Creating..." : "Create Login"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
